import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { clsx } from 'clsx';

import { Button, DropdownMenu, EmptyState, Pagination, Table, Tooltip } from '@treezor/design-system';

import { PER_PAGE_OPERATIONS } from '@/features/wallets/constants';
import { useWalletsOperationsData } from '@/features/wallets/hooks/useWalletsOperationsData';
import { DownloadOperations } from '@/features/wallets/components/DownloadOperations/DownloadOperations';
import { IAttributeConfig } from '@/features/wallets/store/models/wallet.model';
import { operationsZones } from '@/store/models/operation.model';
import { MenuWalletOperationsFilters } from '@/features/wallets/components/WalletOperations/MenuWalletOperationsFilters';
import { getAttributesValues, useAttributes } from '@/features/wallets/components/WalletOperations/operationsHelpers';
import { getCurrentWalletOperations } from '@/features/wallets/store/actions/wallet.actions';

import { useProfileContext } from '@/features/context/ProfileContext';
import { useAppSelector, useAppDispatch } from '@/hooks/redux-helpers';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';
import { useAccesControl } from '@/hooks/useAccessControl';
import { useAction } from '@/hooks/useAction';
import { useCursor } from '@/hooks/useCursor';

import { P2PTransfer } from '@/features/P2PTransfer/components/P2PTransfer';

import { PayoutAction } from '@/features/payouts/components/Payout';

import { featuresSlugs } from '@/features/roles/store/models/features.model';
import { selectCurrentProfileWalletByUserId } from '@/store/selectors';

export type TOperationDetail = Record<string, string | number | ReactNode>;

export const WalletOperations = ({ hasActifWallets }: { hasActifWallets: boolean }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const dashboardConfig = useDashboardConfig();
  const { hasAccessToFeature } = useAccesControl();
  const { displayActionFromConfig } = useAction();

  const initialOperationColumns = useAttributes({
    isCrypto: !!dashboardConfig.api.encryption,
    zone: operationsZones.HEADER_TABLE,
    setChecked: true,
  });
  const [walletColumns, setWalletColumns] = useState<IAttributeConfig[]>(initialOperationColumns);

  const { user, updateMetadata } = useProfileContext();

  const userId = user.userId;

  const [operationsFilters, setOperationsFilters] = useState<IAttributeConfig[] | undefined>(undefined);

  const currentWallet = useAppSelector((state) => selectCurrentProfileWalletByUserId(state, userId)?.state?.wallet);
  const isLoading = useAppSelector(
    (state) => selectCurrentProfileWalletByUserId(state, userId)?.state?.paginateOperations?.isLoading,
  );
  const paginateOperations = useAppSelector(
    (state) => selectCurrentProfileWalletByUserId(state, userId)?.state?.paginateOperations?.state?.elements,
  );
  const cursor = useAppSelector(
    (state) => selectCurrentProfileWalletByUserId(state, userId)?.state?.paginateOperations?.state?.cursor,
  );

  const isCurrentWalletLoading = useMemo(() => isLoading || !currentWallet?.userId, [currentWallet?.userId, isLoading]);

  useEffect(() => {
    if (currentWallet?.walletId) dispatch(getCurrentWalletOperations(currentWallet.walletId, userId, {}, null));
  }, [dispatch, currentWallet?.walletId, userId]);

  useEffect(() => {
    updateMetadata({ walletColumns });
  }, [walletColumns, updateMetadata]);

  const handleFormFiltersChange = (filters?: IAttributeConfig[]) => {
    setOperationsFilters(filters || []);
    if (currentWallet)
      dispatch(getCurrentWalletOperations(currentWallet.walletId, userId, getAttributesValues(filters)));
  };

  const handleNavitation = useCallback(
    (cursorId: string) => {
      dispatch(
        getCurrentWalletOperations(currentWallet?.walletId, userId, getAttributesValues(operationsFilters), cursorId),
      );
    },
    [currentWallet?.walletId, userId, operationsFilters, dispatch],
  );

  const pagination = useCursor(cursor, PER_PAGE_OPERATIONS, handleNavitation);

  const handleColumnsChange = useCallback(
    (columns: IAttributeConfig[]) => {
      setWalletColumns(columns);
    },
    [setWalletColumns],
  );

  const actions = [];

  if (displayActionFromConfig('payout') && hasAccessToFeature(featuresSlugs.PAYOUT)) {
    actions.push({ component: <PayoutAction /> });
  }
  if (displayActionFromConfig('transfer') && hasAccessToFeature(featuresSlugs.WALLET_TO_WALLET_TRANSFER)) {
    actions.push({ component: <P2PTransfer user={user} /> });
  }

  const { columns, rows } = useWalletsOperationsData(walletColumns, paginateOperations, userId);

  const hasPagination = !!cursor?.next || !!cursor?.prev;

  return (
    <div className="basis-4/5 overflow-x-auto" data-test-id="wallet-operation">
      <div className="flex justify-between items-center mb-5">
        <h5 className="typo-h5-bold text-secondary">
          {t(`pages.currentProfiles.wallets.wallet.operationTableHeader`)}
        </h5>
        <div className="flex justify-end">
          {actions?.length > 0 && (
            <>
              {hasActifWallets ? (
                <DropdownMenu
                  variant="primary"
                  size="s"
                  closeOnSelect
                  disabled={false}
                  items={actions}
                  testId="make-payment"
                >
                  {t('pages.currentProfiles.wallets.actions.makePayment')}
                </DropdownMenu>
              ) : (
                <Tooltip
                  contentClassName="w-13"
                  messageContent={t('pages.currentProfiles.wallets.actions.tooltip.noWalletsActif')}
                >
                  <Button disabled={true} size="s" testId="make-payment">
                    {t('pages.currentProfiles.wallets.actions.makePayment')}
                  </Button>
                </Tooltip>
              )}
            </>
          )}
          {currentWallet && displayActionFromConfig('downloadOperations') && (
            <div className="ml-3">
              <DownloadOperations currentWalletId={Number(currentWallet.walletId)} />
            </div>
          )}
        </div>
      </div>
      <div className="overflow-x-auto mt-4">
        <div className="flex gap-4 items-center py-4 px-5">
          <MenuWalletOperationsFilters
            onFormFiltersChange={handleFormFiltersChange}
            onColumnChange={handleColumnsChange}
            selectedColumns={walletColumns}
          />
        </div>
        {paginateOperations?.length || isCurrentWalletLoading ? (
          <div className="flex flex-col items-end ">
            <Table
              columns={columns}
              rows={rows}
              hasStickyHeader
              className={clsx(
                'bg-primary  lightScrollBar',
                hasPagination ? 'h-[calc(100vh-490px)]' : 'h-[calc(100vh-450px)]',
              )}
            />
            {hasPagination && (
              <Pagination
                {...pagination}
                isFirstButtonVisible
                isLastButtonVisible={false}
                isPagesVisible={false}
                className="flex justify-end"
                isDisabled={false}
              />
            )}
          </div>
        ) : (
          <EmptyState
            size="l"
            title={t('pages.currentProfiles.wallets.table.noOperations')}
            state={t('pages.currentProfiles.wallets.table.disclaimer')}
            className="pt-8"
          />
        )}
      </div>
    </div>
  );
};
